/**
 * Learner actions — state updates, answer recording, XP, streaks, mastery.
 *
 * Extracted from store.ts as part of T1 (store.ts opsplitsen).
 */

import type { SetFn, GetFn, AppStoreState } from './types'
import { QUESTION_BY_ID, getFamilyDefinition } from '../../content'
import type { GroupId } from '../../config/systemConfig'
import {
  LEVEL_THRESHOLDS,
  XP_REWARDS,
  getLevelForXP,
  calculateXP,
} from '../../config/constants'
import { getActiveAccount } from '../../accounts'
import {
  updateQuestionState,
  getQuestionStateKey,
  updateFamilyMastery,
  getFamilyMasteryKey,
  getPlacementKey,
} from '../../mastery'
import { warn } from '../../utils/logger'

type LearnerActions = Pick<
  AppStoreState,
  | 'updateLearnerState'
  | 'markCheatSheetViewed'
  | 'recordAnswer'
  | 'clearPendingLevelUp'
  | 'devJumpToLevel'
  | 'savePlacementResult'
  | 'markAchievementUnlocked'
  | 'updateDailyChallengeProgress'
>

export function createLearnerActions(set: SetFn, get: GetFn): LearnerActions {
  return {
    updateLearnerState: (updater) => {
      set((s) => {
        if (!s.multiAccountState) return s
        const active = getActiveAccount(s.multiAccountState.accounts)
        if (!active || active.role !== 'learner') {
          warn('Cannot update state for non-learner account')
          return s
        }
        return {
          multiAccountState: {
            ...s.multiAccountState,
            learnerData: {
              ...s.multiAccountState.learnerData,
              [active.id]: updater(s.multiAccountState.learnerData[active.id]),
            },
          },
        }
      })
    },

    markCheatSheetViewed: (moduleId) => {
      get().updateLearnerState((state) => {
        const current = state.progress[moduleId] || { cheatSheetViewed: false }
        return {
          ...state,
          progress: {
            ...state.progress,
            [moduleId]: { ...current, cheatSheetViewed: true },
          },
        }
      })
    },

    markAchievementUnlocked: (achievementId) => {
      get().updateLearnerState((state) => {
        const currentUnlocked = state.stats.unlockedAchievements || []
        if (currentUnlocked.includes(achievementId)) return state

        return {
          ...state,
          stats: {
            ...state.stats,
            unlockedAchievements: [...currentUnlocked, achievementId],
          },
        }
      })
    },

    recordAnswer: (
      moduleId,
      questionId,
      isCorrect,
      userChoice,
      payload,
      errorType,
      delta,
      timeMs,
    ) => {
      const selectedGroup = get().selectedGroup
      get().updateLearnerState((prev) => {
        const question = QUESTION_BY_ID.get(questionId)
        const questionGroup = question?.group || selectedGroup

        // Use group-scoped key for question state
        const stateKey = getQuestionStateKey(questionId, questionGroup)
        const currentQuestionState = prev.questionStates[stateKey]
        const newQuestionState = updateQuestionState(
          currentQuestionState,
          questionId,
          isCorrect,
          timeMs,
        )
        const newQuestionStates = {
          ...prev.questionStates,
          [stateKey]: newQuestionState,
        }

        // Update familyId mastery tracking (CANONICAL MASTERY SOURCE - GROUP-SCOPED)
        let newFamilyMastery = prev.familyMastery ?? {}
        if (question?.familyId) {
          const familyId = question.familyId
          const questionTier =
            question.tier ?? getFamilyDefinition(familyId)?.tier ?? 1
          const masteryKey = getFamilyMasteryKey(familyId, selectedGroup)
          const currentFamilyState = newFamilyMastery[masteryKey]
          const updatedFamilyState = updateFamilyMastery(
            currentFamilyState,
            familyId,
            isCorrect,
            questionId,
            questionTier,
            payload,
            errorType,
            delta,
          )
          newFamilyMastery = {
            ...newFamilyMastery,
            [masteryKey]: updatedFamilyState,
          }
        }

        // Streak tracking
        const currentStreakState = prev.streak || {
          currentTotalStreak: 0,
          longestTotalStreak: 0,
        }

        let newCurrentStreak = currentStreakState.currentTotalStreak
        if (isCorrect) {
          newCurrentStreak += 1
        } else {
          newCurrentStreak = 0
        }

        const newLongestStreak = Math.max(
          currentStreakState.longestTotalStreak,
          newCurrentStreak,
        )

        const newStreakState = {
          currentTotalStreak: newCurrentStreak,
          longestTotalStreak: newLongestStreak,
          lastStreakQuestionId: questionId,
        }

        // Daily goal tracking
        const currentDailyStreak = prev.dailyStreak || {
          dailyGoalTarget: 10,
          todayQuestionsAnswered: 0,
          todayGoalReached: false,
          weeklyGoalTarget: 3,
          weeklyGoalReached: false,
          currentWeekDays: 0,
          consecutiveFullWeeks: 0,
          longestWeekStreak: 0,
          lastActiveDate: new Date().toISOString().split('T')[0],
          weeklyHistory: {},
        }

        // Simple daily increment (midnight reset is handled in useAppStore)
        const newTodayQuestions = currentDailyStreak.todayQuestionsAnswered + 1
        const newlyReachedGoal =
          !currentDailyStreak.todayGoalReached &&
          newTodayQuestions >= currentDailyStreak.dailyGoalTarget

        let newCurrentWeekDays = currentDailyStreak.currentWeekDays
        let newlyReachedWeeklyGoal = false

        if (newlyReachedGoal) {
          newCurrentWeekDays += 1
          if (
            !currentDailyStreak.weeklyGoalReached &&
            newCurrentWeekDays >= currentDailyStreak.weeklyGoalTarget
          ) {
            newlyReachedWeeklyGoal = true
          }
        }

        const newDailyStreak = {
          ...currentDailyStreak,
          todayQuestionsAnswered: newTodayQuestions,
          todayGoalReached:
            currentDailyStreak.todayGoalReached || newlyReachedGoal,
          currentWeekDays: newCurrentWeekDays,
          weeklyGoalReached:
            currentDailyStreak.weeklyGoalReached || newlyReachedWeeklyGoal,
          // HC-7/HC-8 fix: track lifetime daily goals reached
          totalDailyGoalsReached:
            (currentDailyStreak.totalDailyGoalsReached ?? 0) +
            (newlyReachedGoal ? 1 : 0),
        }

        // XP calculation using canonical helper (D-SCAN D-4)
        let xpGain = calculateXP(isCorrect, question?.tier)

        // Apply bonuses
        if (newlyReachedGoal) {
          xpGain += XP_REWARDS.DAILY_GOAL
        }
        if (newlyReachedWeeklyGoal) {
          xpGain +=
            currentDailyStreak.weeklyGoalTarget >= 5
              ? XP_REWARDS.WEEK_5_DAYS
              : XP_REWARDS.WEEK_3_DAYS
        }

        const newXP = prev.stats.xp + xpGain
        const oldLevel = prev.stats.level
        const newLevel = getLevelForXP(newXP)
        const pendingLevelUp =
          newLevel > oldLevel ? newLevel : prev.pendingLevelUp

        return {
          ...prev,
          stats: { ...prev.stats, xp: newXP, level: newLevel },
          progress: prev.progress,
          questionStates: newQuestionStates,
          familyMastery: newFamilyMastery,
          streak: newStreakState,
          dailyStreak: newDailyStreak,
          pendingLevelUp,
        }
      })
    },

    clearPendingLevelUp: () => {
      get().updateLearnerState((prev) => ({
        ...prev,
        pendingLevelUp: undefined,
      }))
    },

    devJumpToLevel: (targetLevel) => {
      get().updateLearnerState((prev) => ({
        ...prev,
        stats: {
          ...prev.stats,
          xp: LEVEL_THRESHOLDS[targetLevel - 1] || 0,
          level: targetLevel,
        },
      }))
    },

    /** @deprecated Placement test removed. Kept for backward compatibility with existing localStorage data. */
    savePlacementResult: (result) => {
      get().updateLearnerState((prev) => {
        const key = getPlacementKey(result.moduleId, result.group as GroupId)
        return {
          ...prev,
          placementResults: { ...prev.placementResults, [key]: result },
        }
      })
    },

    updateDailyChallengeProgress: (amount, challengeType, targetCount) => {
      get().updateLearnerState((state) => {
        const today = new Date().toISOString().split('T')[0]
        const currentChallenge = state.dailyChallenge

        // Reset if it's a new day
        const isNewDay = currentChallenge?.lastChallengeDate !== today
        const currentProgress = isNewDay ? 0 : currentChallenge?.progress || 0
        const wasCompleted = isNewDay
          ? false
          : currentChallenge?.completedToday || false

        if (wasCompleted) return state // Already done today

        const newProgress = currentProgress + amount
        const isCompletedNow = newProgress >= targetCount

        let newStats = state.stats
        let newPendingLevelUp = state.pendingLevelUp

        // Award 50 XP if just completed
        if (isCompletedNow) {
          const rewardXP = 50
          const newXP = state.stats.xp + rewardXP
          const newLevel = getLevelForXP(newXP)
          const leveledUp = newLevel > state.stats.level

          newStats = {
            ...state.stats,
            xp: newXP,
            level: newLevel,
          }

          if (leveledUp) {
            newPendingLevelUp = newLevel
          }
        }

        return {
          ...state,
          stats: newStats,
          pendingLevelUp: newPendingLevelUp,
          dailyChallenge: {
            lastChallengeDate: today,
            challengeType,
            progress: newProgress,
            completedToday: isCompletedNow,
          },
        }
      })
    },
  }
}
